import PropTypes from 'prop-types'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { useHistory, useParams } from 'react-router-dom'
import { Loader2 } from 'lucide-react'

import { FileUpload } from '@components/file-upload'
import { useScrollTop } from '../../utils/useScrollTop'
import { AddFileButton } from '@components/buttons'
import { getDocuments, getFile, getInvoiceByReferenceNumber, getWorkOrderByReferenceNumber } from '../../api/api'
import { FILE_UPLOAD, PARAM_NAMES, REDIRECT_URLS } from '../../constants'
import { MIME_TYPES_MAP } from '../../variable-maps'
import { Logger } from '../../logger'
import { AdasBox, AdasButton, AdasContainer, AdasLink, AdasTypography, AdasCircularProgress } from '@components/wrapper-components'
import { StatusEnum } from 'enums/enums'
import { handleDownloadOEMStatementPDF } from 'utils/utils'

const REPORT_KEYS = ['preCalibrationReport', 'oemProcedure', 'wheelAlignmentReport', 'calibrationReport', 'postTestScanReport']
const ADDITIONAL_NAMED_KEYS = ['collisionRepairEstimate', 'calibrationRequiredReport', 'partsInvoice']

// Build a Blob URL out of a fetched file response so the FileUpload tile can use
// it directly (and so we can show one page-level loader instead of one spinner
// per tile + one for the page chrome).
const buildBlobUrl = (data, name = '') => {
  const ext = name?.includes('.') ? name.slice(name.lastIndexOf('.')).toLowerCase() : ''
  const mime = MIME_TYPES_MAP[ext] || 'application/octet-stream'
  const blob = new Blob([data], { type: mime })
  return { url: URL.createObjectURL(blob), size: blob.size }
}

export const DocumentPage = (props) => {
  useScrollTop()
  const { fromInvoice = false } = props
  const { [PARAM_NAMES.REFERENCE_NUMBER]: referenceNumber } = useParams()
  const history = useHistory()
  const [workOrderData, setWorkOrderData] = useState({})
  const [pageLoading, setPageLoading] = useState(true)
  const [statementLoading, setStatementLoading] = useState(false)

  const [documents, setDocuments] = useState({
    preCalibrationReport: '',
    oemProcedure: '',
    wheelAlignmentReport: '',
    calibrationReport: '',
    postTestScanReport: '',
    collisionRepairEstimate: '',
    calibrationRequiredReport: '',
    partsInvoice: '',
    qwiOemPositionStatements: []
  })
  const [additionalDocuments, setAdditionalDocuments] = useState([])
  const [loadedAdditionalDocs, setLoadedAdditionalDocs] = useState([])

  const redirectToForbiddenPage = () => history.replace(REDIRECT_URLS.FORBIDDEN_PAGE)

  const removeAdditionalFileHandler = useCallback((index) => {
    setAdditionalDocuments((prev) => {
      const data = [...prev]
      if (data[index]) data[index].removed = true
      return data
    })
  }, [])

  const removeLoadedAdditionalFileHandler = useCallback((index) => {
    setLoadedAdditionalDocs((prev) => {
      const data = [...prev]
      if (data[index]) data[index].removed = true
      return data
    })
  }, [])

  const removeFileHandler = useCallback((fileName) => {
    if (!fileName) return
    setDocuments((prev) => ({ ...prev, [fileName]: null }))
  }, [])

  // Single bootstrap effect: fetch the WO/invoice, then the documents list, then
  // prefetch every attachment blob in parallel. Only flip pageLoading off after
  // all of them resolve so the user sees one loader instead of cascading ones.
  useEffect(() => {
    if (!referenceNumber) return undefined
    let cancelled = false
    const blobUrls = []

    const bootstrap = async () => {
      setPageLoading(true)
      try {
        let parentId = null
        let parentStatus = null
        if (fromInvoice) {
          const response = await getInvoiceByReferenceNumber({ referenceNumber })
          if (cancelled) return
          if (response?.status !== 200 || !response.data?.invoiceDto) {
            redirectToForbiddenPage()
            return
          }
          parentId = response.data.workOrderDto.id
          parentStatus = response.data.invoiceDto.status
        } else {
          const response = await getWorkOrderByReferenceNumber({ referenceNumber })
          if (cancelled) return
          if (response?.status !== 200) {
            redirectToForbiddenPage()
            return
          }
          if (StatusEnum[response.data?.workOrder?.status] === StatusEnum.MOVED) {
            redirectToForbiddenPage()
            return
          }
          parentId = response.data.workOrder.id
          parentStatus = response.data.workOrder.status
        }
        const parent = { id: parentId, status: parentStatus }
        setWorkOrderData(parent)
        if (!parentId) return

        const docsRes = await getDocuments({ type: 'workorders', id: parentId })
        if (cancelled) return
        const docData = docsRes?.data || {}

        const named = {
          preCalibrationReport: docData.preCalibrationReport || '',
          oemProcedure: docData.oemProcedure || '',
          wheelAlignmentReport: docData.wheelAlignmentReport || '',
          calibrationReport: docData.calibrationReport || '',
          postTestScanReport: docData.postTestScanReport || '',
          collisionRepairEstimate: docData.collisionRepairEstimate || '',
          calibrationRequiredReport: docData.calibrationRequiredReport || '',
          partsInvoice: docData.partsInvoice || '',
          qwiOemPositionStatements: docData.qwiOemPositionStatements || []
        }
        const additional = Array.isArray(docData.additionalDocuments) ? docData.additionalDocuments : []

        const namedEntries = Object.entries(named).filter(([k, v]) => k !== 'qwiOemPositionStatements' && v && v.id)
        const additionalEntries = additional.filter((d) => d && d.id)

        // Fire all blob fetches in parallel — one shared loader.
        const namedBlobResults = await Promise.all(
          namedEntries.map(async ([key, meta]) => {
            try {
              const r = await getFile({ attachmentId: meta.id })
              const { url, size } = buildBlobUrl(r.data, meta.name)
              blobUrls.push(url)
              return [key, { ...meta, preloadedUrl: url, preloadedSize: size }]
            } catch (err) {
              Logger.error({ message: err, payload: { file: 'DocumentPage/prefetch named', id: meta.id } })
              return [key, meta]
            }
          })
        )

        const additionalBlobResults = await Promise.all(
          additionalEntries.map(async (meta) => {
            try {
              const r = await getFile({ attachmentId: meta.id })
              const { url, size } = buildBlobUrl(r.data, meta.name)
              blobUrls.push(url)
              return { ...meta, preloadedUrl: url, preloadedSize: size }
            } catch (err) {
              Logger.error({ message: err, payload: { file: 'DocumentPage/prefetch additional', id: meta.id } })
              return meta
            }
          })
        )

        if (cancelled) return
        const mergedNamed = { ...named }
        namedBlobResults.forEach(([key, val]) => {
          mergedNamed[key] = val
        })
        setDocuments((prev) => ({ ...prev, ...mergedNamed }))
        setLoadedAdditionalDocs(additionalBlobResults)
      } catch (err) {
        Logger.error({ message: err, payload: { referenceNumber, file: 'DocumentPage/bootstrap' } })
      } finally {
        if (!cancelled) setPageLoading(false)
      }
    }

    bootstrap()

    return () => {
      cancelled = true
      blobUrls.forEach((u) => URL.revokeObjectURL(u))
    }
  }, [referenceNumber, fromInvoice])

  const handleOemStatementClick = useCallback(async (event) => {
    const id = event.target.id
    setStatementLoading(id)
    try {
      await handleDownloadOEMStatementPDF(id)
    } catch (error) {
      Logger.error({ message: error, payload: { id, path: 'DocumentPage/handleOemStatementClick' } })
    } finally {
      setStatementLoading(false)
    }
  }, [])

  const disableEdit = StatusEnum[workOrderData.status] === StatusEnum.CLOSED || fromInvoice

  // Track how many of the visible slots have an attached file across both sections.
  const { reportsAttached, reportsTotal, additionalAttached, additionalTotal } = useMemo(() => {
    const reportAttached = REPORT_KEYS.reduce((n, k) => n + (documents[k] ? 1 : 0), 0)
    const namedAdditionalAttached = ADDITIONAL_NAMED_KEYS.reduce((n, k) => n + (documents[k] ? 1 : 0), 0)
    const loadedActive = loadedAdditionalDocs.filter((d) => d && !d.removed).length
    const additionalActive = additionalDocuments.filter((d) => d && !d.removed).length
    const additionalSlots = ADDITIONAL_NAMED_KEYS.length + loadedAdditionalDocs.length + additionalDocuments.length + 1
    return {
      reportsAttached: reportAttached,
      reportsTotal: REPORT_KEYS.length,
      additionalAttached: namedAdditionalAttached + loadedActive + additionalActive,
      additionalTotal: additionalSlots
    }
  }, [documents, loadedAdditionalDocs, additionalDocuments])

  if (pageLoading) {
    return (
      <AdasContainer maxWidth={false}>
        <AdasBox className='flex min-h-[60vh] flex-col items-center justify-center gap-3'>
          <Loader2 className='h-7 w-7 animate-spin text-[#1F6FEB]' />
          <AdasTypography className='text-[14px] text-[#475569]'>Loading documents...</AdasTypography>
        </AdasBox>
      </AdasContainer>
    )
  }

  return (
    <AdasContainer maxWidth={false}>
      <AdasBox className='mb-[60px] mt-5'>
        {/* Header — Back link + title (desktop only); mobile shows the meta line */}
        <AdasBox className='mb-4'>
          <AdasBox className='hidden min-[435px]:block'>
            <button
              type='button'
              onClick={() => history.goBack()}
              className='mb-1 inline-flex items-center gap-1.5 text-[13px] font-medium text-[#1F6FEB] hover:underline'
            >
              Back to {fromInvoice ? 'Invoice Summary' : 'WO Summary'}
            </button>
            <AdasTypography className='text-[28px] font-bold leading-tight text-[#0A1628]'>Documents</AdasTypography>
          </AdasBox>
        </AdasBox>

        {/* Reports card */}
        <AdasBox className='mb-4 rounded-xl border border-[#EBEDF0] bg-white px-6 py-5 shadow-sm'>
          <AdasBox className='mb-4 flex items-center justify-between'>
            <AdasTypography className='text-[12px] font-semibold uppercase tracking-wide text-[#6B7280]'>Reports</AdasTypography>
            <AdasTypography className='text-[13px] text-[#475569]'>
              <span className='font-bold text-[#0A1628]'>{reportsAttached}</span>
              <span className='ml-1'>of {reportsTotal} attached</span>
            </AdasTypography>
          </AdasBox>

          <AdasBox className='flex flex-col gap-3'>
            <FileUpload
              variant='row'
              type='workorders'
              removeFile={() => removeFileHandler('preCalibrationReport')}
              existingData={documents.preCalibrationReport}
              workOrderData={workOrderData}
              title={FILE_UPLOAD.TITLE.UPLOAD_PRE_CALIBRATION_SCAN_REPORT}
              label={FILE_UPLOAD.LABEL.PRE_CALIBRATION_SCAN_REPORT}
              description='Diagnostic scan run before any calibration work'
              typeLabel={FILE_UPLOAD.TYPE_LABEL.PRE_CALIBRATION_SCAN_REPORT}
              disabled={disableEdit}
            />
            <FileUpload
              variant='row'
              type='workorders'
              removeFile={() => removeFileHandler('oemProcedure')}
              existingData={documents.oemProcedure}
              workOrderData={workOrderData}
              title={FILE_UPLOAD.TITLE.UPLOAD_OEM_PROCEDURE}
              label={FILE_UPLOAD.LABEL.OEM_PROCEDURE}
              description='Manufacturer procedure followed during the calibration'
              typeLabel={FILE_UPLOAD.TYPE_LABEL.OEM_PROCEDURE}
              disabled={disableEdit}
            />
            <FileUpload
              variant='row'
              type='workorders'
              removeFile={() => removeFileHandler('wheelAlignmentReport')}
              existingData={documents.wheelAlignmentReport}
              workOrderData={workOrderData}
              title={FILE_UPLOAD.TITLE.UPLOAD_WHEEL_ALIGNMENT_REPORT}
              label={FILE_UPLOAD.LABEL.WHEEL_ALIGNMENT_REPORT}
              description='Pre-calibration wheel alignment audit results'
              typeLabel={FILE_UPLOAD.TYPE_LABEL.WHEEL_ALIGNMENT_REPORT}
              disabled={disableEdit}
            />
            <FileUpload
              variant='row'
              type='workorders'
              removeFile={() => removeFileHandler('calibrationReport')}
              existingData={documents.calibrationReport}
              workOrderData={workOrderData}
              title={FILE_UPLOAD.TITLE.UPLOAD_CALIBRATION_REPORT}
              label={FILE_UPLOAD.LABEL.CALIBRATION_REPORT}
              description='Final calibration report from the scan tool'
              typeLabel={FILE_UPLOAD.TYPE_LABEL.CALIBRATION_REPORT}
              disabled={disableEdit}
            />
            <FileUpload
              variant='row'
              type='workorders'
              removeFile={() => removeFileHandler('postTestScanReport')}
              existingData={documents.postTestScanReport}
              workOrderData={workOrderData}
              title={FILE_UPLOAD.TITLE.UPLOAD_POST_TEST_DRIVE_SCAN_REPORT}
              label={FILE_UPLOAD.LABEL.POST_TEST_DRIVE_SCAN_REPORT}
              description='Diagnostic scan run after the post-calibration test drive'
              typeLabel={FILE_UPLOAD.TYPE_LABEL.POST_TEST_DRIVE_SCAN_REPORT}
              disabled={disableEdit}
            />
          </AdasBox>
        </AdasBox>

        {/* Additional Documents card */}
        <AdasBox className='mb-4 rounded-xl border border-[#EBEDF0] bg-white px-6 py-5 shadow-sm'>
          <AdasBox className='mb-4 flex items-center justify-between'>
            <AdasTypography className='text-[12px] font-semibold uppercase tracking-wide text-[#6B7280]'>
              Additional Documents
            </AdasTypography>
            <AdasTypography className='text-[13px] text-[#475569]'>
              <span className='font-bold text-[#0A1628]'>{additionalAttached}</span>
              <span className='ml-1'>of {additionalTotal} attached</span>
            </AdasTypography>
          </AdasBox>

          <AdasBox className='flex flex-col gap-3'>
            <FileUpload
              variant='row'
              type='workorders'
              removeFile={() => removeFileHandler('collisionRepairEstimate')}
              existingData={documents.collisionRepairEstimate}
              workOrderData={workOrderData}
              title={FILE_UPLOAD.TITLE.UPLOAD_COLLISION_REPAIR_ESTIMATE}
              label={FILE_UPLOAD.LABEL.COLLISION_REPAIR_ESTIMATE}
              description='PDF from CCC, Mitchell, Audatex — used to reconstruct line items'
              typeLabel={FILE_UPLOAD.TYPE_LABEL.PRE_CALIBRATION_REPAIR_ESTIMATE_SCAN}
              disabled={disableEdit}
            />
            <FileUpload
              variant='row'
              type='workorders'
              removeFile={() => removeFileHandler('calibrationRequiredReport')}
              existingData={documents.calibrationRequiredReport}
              workOrderData={workOrderData}
              title={FILE_UPLOAD.TITLE.UPLOAD_CALIBRATION_REQUIRED_REPORT}
              label={FILE_UPLOAD.LABEL.CALIBRATION_REQUIRED_REPORT}
              description='OEM scan tool report — confirms which calibrations the vehicle needs'
              typeLabel={FILE_UPLOAD.TYPE_LABEL.CALIBRATION_REQUIRED_REPORT}
              disabled={disableEdit}
            />
            <FileUpload
              variant='row'
              type='workorders'
              removeFile={() => removeFileHandler('partsInvoice')}
              existingData={documents.partsInvoice}
              workOrderData={workOrderData}
              title={FILE_UPLOAD.TITLE.UPLOAD_PARTS_INVOICE}
              label={FILE_UPLOAD.LABEL.PARTS_INVOICE}
              description='Invoice from your parts supplier'
              typeLabel={FILE_UPLOAD.TYPE_LABEL.PARTS_INVOICE}
              disabled={disableEdit}
            />
            {loadedAdditionalDocs.map((doc, index) =>
              doc && !doc.removed ? (
                <FileUpload
                  key={`loaded-${index}`}
                  variant='row'
                  type='workorders'
                  removeFile={() => removeLoadedAdditionalFileHandler(index)}
                  existingData={doc}
                  workOrderData={workOrderData}
                  title={`custom_document_${index}`}
                  typeLabel={FILE_UPLOAD.TYPE_LABEL.ADDITIONAL_DOCUMENT}
                  label={FILE_UPLOAD.LABEL.ADDITIONAL_DOCUMENT}
                  disabled={disableEdit}
                />
              ) : null
            )}
            {additionalDocuments.map((doc, index) =>
              doc && !doc.removed ? (
                <FileUpload
                  key={`additional-${index}`}
                  variant='row'
                  type='workorders'
                  removeFile={() => removeAdditionalFileHandler(index)}
                  additionalDocument={doc}
                  workOrderData={workOrderData}
                  title={`custom_document_${index}`}
                  typeLabel={FILE_UPLOAD.TYPE_LABEL.ADDITIONAL_DOCUMENT}
                  label={FILE_UPLOAD.LABEL.ADDITIONAL_DOCUMENT}
                  disabled={disableEdit}
                />
              ) : null
            )}
            <AddFileButton setAdditionalDocuments={setAdditionalDocuments} disabled={disableEdit} />
          </AdasBox>
        </AdasBox>

        {/* OEM Position Statement Lines */}
        {documents?.qwiOemPositionStatements?.length > 0 && (
          <AdasBox className='mb-4 rounded-xl border border-[#EBEDF0] bg-white px-6 py-5 shadow-sm'>
            <AdasTypography className='mb-3 text-[12px] font-semibold uppercase tracking-wide text-[#6B7280]'>
              OEM Position Statement Lines
            </AdasTypography>
            {documents.qwiOemPositionStatements.map((data, index) => (
              <AdasBox key={index} className='mb-2 flex items-center'>
                {statementLoading == data.oemPositionStatements.id && <AdasCircularProgress size={15} className='mr-[10px]' />}
                <AdasLink
                  id={data.oemPositionStatements.id}
                  className='block cursor-pointer overflow-hidden text-sm text-[#1F6FEB] no-underline whitespace-nowrap hover:underline'
                  target='_blank'
                  rel='noopener noreferrer'
                  onClick={handleOemStatementClick}
                >
                  {data.oemPositionStatements.name}
                </AdasLink>
              </AdasBox>
            ))}
          </AdasBox>
        )}

        {/* Footer — DONE (filled navy) + Cancel (outlined) */}
        <AdasBox className='flex items-center gap-3'>
          <AdasButton
            variant='contained'
            onClick={() => history.goBack()}
            className='rounded-lg bg-[#0A1628] px-6 font-semibold text-white hover:bg-[#1F2D3D]'
          >
            DONE
          </AdasButton>
          <AdasButton variant='outlined' onClick={() => history.goBack()} className='rounded-lg px-6 font-semibold'>
            Cancel
          </AdasButton>
        </AdasBox>
      </AdasBox>
    </AdasContainer>
  )
}

DocumentPage.propTypes = {
  fromInvoice: PropTypes.bool
}
